import { CategoryOptionsListProps } from "../proctoringOptionsGrid";
import OptionBlock from "../optionBlock";
import i18nPackage from "../../../i18n/i18n";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckBoxMenuSubItem } from "../option-menus/checkBoxMenuSubItem";
import { IncognitoWarningModal } from "../option-modals/incognito-warning-modal";
import { useProctoringOptionContext } from "../../context/EditQuizContext";
import { useProctoringHelpers } from "../hooks/useProctoringHelpers";
import { ProctoringOption } from "@verity/proctoring-option/types";
import { Trans } from "react-i18next";
import TabWhiteList from "../option-lists/TabWhiteList";
import KeyboardBlockList from "../option-lists/KeyboardBlockList";
import GeofenceList from "../option-lists/GeofenceList";
import { isGeofenceEqual } from "../option-lists/geofenceListHelpers";

function OneScreenOption() {
	const { proctoringOption, setProctoringOption, enforcedProfilesOnly } = useProctoringOptionContext();
	const { isOptionAllowed, getDisabledMessage, getPartiallySupportedPlatforms } = useProctoringHelpers();

	const { oneScreenEnforcementMode, hideOtherWindows } = proctoringOption;
	const [bypassWasChecked, setBypassWasChecked] = useState<boolean>(oneScreenEnforcementMode !== "strict");

	const notAllowed = !isOptionAllowed("oneScreenEnforcementMode");
	return (
		<OptionBlock
			optionKey="oneScreenEnforcementMode"
			checked={oneScreenEnforcementMode !== "off" && !enforcedProfilesOnly && !notAllowed}
			updateState={(checked) =>
				setProctoringOption({
					...proctoringOption,
					oneScreenEnforcementMode: checked ? (bypassWasChecked ? "normal" : "strict") : "off",
				})
			}
			disabled={notAllowed || hideOtherWindows || enforcedProfilesOnly}
			disabledMessage={
				getDisabledMessage("oneScreenEnforcementMode") ??
				i18nPackage.t("editQuizPage.configurationStep.otherActivitiesTSX.onlyOneScreen")
			}
			menuItems={
				<>
					{isOptionAllowed("oneScreenEnforcementMode", "normal") &&
					isOptionAllowed("oneScreenEnforcementMode", "strict") ? (
						<CheckBoxMenuSubItem
							label={i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.bypassModeText")}
							checked={bypassWasChecked}
							onChange={(checked) => {
								setProctoringOption({
									...proctoringOption,
									oneScreenEnforcementMode: checked ? "normal" : "strict",
								});
								setBypassWasChecked(checked);
							}}
							partiallySupportedPlatforms={getPartiallySupportedPlatforms("oneScreenEnforcementMode", "strict")}
						/>
					) : undefined}
				</>
			}
		/>
	);
}

function RecordInputOption() {
	const { proctoringOption, setProctoringOption, isMobileLayout } = useProctoringOptionContext();
	const { isOptionAllowed } = useProctoringHelpers();

	const { recordInput, keyboardShortcutBlockList } = proctoringOption;
	const [focusList, setFocusList] = useState<boolean>(false);

	const checked = Boolean(recordInput);
	const keyboadListAllowed = isOptionAllowed("keyboardShortcutBlockList");
	return (
		<OptionBlock
			optionKey="recordInput"
			checked={checked}
			updateState={(checked) => {
				setProctoringOption({
					...proctoringOption,
					recordInput: checked,
					keyboardShortcutBlockList: checked ? proctoringOption.keyboardShortcutBlockList : undefined,
				});
				setFocusList(checked);
			}}
		>
			{keyboadListAllowed && checked && (
				<KeyboardBlockList
					initialBlockList={keyboardShortcutBlockList ?? []}
					onUpdate={(shortcuts) => setProctoringOption({ ...proctoringOption, keyboardShortcutBlockList: shortcuts })}
					focusOnMount={focusList && !isMobileLayout}
					onFocus={() => setFocusList(false)}
				/>
			)}
		</OptionBlock>
	);
}

function EnableGeofenceOption({ savedGeofences }: { savedGeofences: CategoryOptionsListProps["data"]["geofences"] }) {
	const { proctoringOption, setProctoringOption, isMobileLayout } = useProctoringOptionContext();

	const { enableGeofence, geofenceLocations } = proctoringOption;

	const checked = Boolean(enableGeofence);
	const [focusList, setFocusList] = useState<boolean>(false);

	const selectedGeofence = useMemo(
		() => (geofenceLocations && geofenceLocations.length > 0 ? geofenceLocations[0] : undefined),
		[geofenceLocations],
	);

	// Is the selected geofence, a user custom made geofence?
	const isCustomGeofence = useMemo(
		() => (selectedGeofence ? !savedGeofences.some((g) => isGeofenceEqual(g, selectedGeofence)) : false),
		[savedGeofences, selectedGeofence],
	);

	const displayedGeofences = useMemo(() => {
		// If no geofence is selected, just show the saved geofencces
		if (!selectedGeofence) {
			return savedGeofences;
		}

		if (isCustomGeofence) {
			// Show just the custom geofence
			return [selectedGeofence];
		} else {
			// If not custom, show all saved geofences
			return savedGeofences;
		}
	}, [isCustomGeofence, savedGeofences, selectedGeofence]);

	return (
		<OptionBlock
			optionKey="enableGeofence"
			checked={checked}
			updateState={(checked) => {
				setProctoringOption({
					...proctoringOption,
					enableGeofence: checked,
					geofenceLocations: checked ? proctoringOption.geofenceLocations : undefined,
				});
				setFocusList(checked);
			}}
			hint={
				savedGeofences.length === 0 && checked
					? i18nPackage.t("editQuizPage.configurationStep.otherActivitiesTSX.geofenceHint")
					: undefined
			}
		>
			{checked && (
				<GeofenceList
					key={isCustomGeofence ? "quizCustom" : "quizSaved"}
					initialGeofences={displayedGeofences}
					initialSelectedGeofence={selectedGeofence}
					mode={isCustomGeofence ? "quizCustom" : "quizSaved"}
					onSelectGeofence={(selectedGeofence) => {
						setProctoringOption({
							...proctoringOption,
							// Perhaps in the future, we will support multiple geofences
							geofenceLocations: selectedGeofence ? [selectedGeofence] : [],
						});
					}}
					onCancelFirstAdd={() => {
						if (savedGeofences.length > 0) {
							// Choose first default geofence
							setProctoringOption({
								...proctoringOption,
								geofenceLocations: [savedGeofences[0]],
							});
						} else {
							// UI team wants to turn off option if they dont choose a geofence
							setProctoringOption({ ...proctoringOption, enableGeofence: false, geofenceLocations: [] });
						}
					}}
					focusOnMount={focusList && !isMobileLayout}
					onFocus={() => setFocusList(false)}
				/>
			)}
		</OptionBlock>
	);
}

function DetectSidePanelOption() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();
	const { detectSidePanel } = proctoringOption;

	return (
		<OptionBlock
			optionKey="detectSidePanel"
			checked={Boolean(detectSidePanel)}
			updateState={(checked) =>
				setProctoringOption({
					...proctoringOption,
					detectSidePanel: checked,
				})
			}
		/>
	);
}

function LogInstalledExtensionsOption() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();
	const { logInstalledExtensions } = proctoringOption;

	return (
		<OptionBlock
			optionKey="logInstalledExtensions"
			checked={Boolean(logInstalledExtensions)}
			updateState={(checked) =>
				setProctoringOption({
					...proctoringOption,
					logInstalledExtensions: checked,
				})
			}
		/>
	);
}

function RecordClipboardOption() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();
	const { recordClipboard } = proctoringOption;

	return (
		<OptionBlock
			optionKey="recordClipboard"
			checked={Boolean(recordClipboard)}
			updateState={(checked) =>
				setProctoringOption({
					...proctoringOption,
					recordClipboard: checked,
				})
			}
		/>
	);
}

function UploadScratchNotesOption() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();

	const { uploadScratchNotes } = proctoringOption;

	return (
		<OptionBlock
			optionKey="uploadScratchNotes"
			checked={Boolean(uploadScratchNotes)}
			updateState={(checked) =>
				setProctoringOption({
					...proctoringOption,
					uploadScratchNotes: checked,
				})
			}
		/>
	);
}

function RestrictDevicesOption() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();
	const { restrictDevices } = proctoringOption;

	return (
		<OptionBlock
			optionKey="restrictDevices"
			checked={Boolean(restrictDevices)}
			updateState={(checked) =>
				setProctoringOption({
					...proctoringOption,
					restrictDevices: checked,
				})
			}
		/>
	);
}

type TabControls = Extract<
	keyof ProctoringOption,
	"closeOpenTabs" | "disableNewTab" | "tabWhiteList" | "recordWebTraffic" | "excludedWebTrafficRecordingUrls"
>;

/**
 * Combines closeOpenTabs, diableNewTabs, tabWhiteList, recordWebTraffic, excludeWebTraffic into one
 */
function TabControlsOption() {
	const { proctoringOption, setProctoringOption, enforcedProfilesOnly } = useProctoringOptionContext();
	const { isOptionAllowed, getPartiallySupportedPlatforms } = useProctoringHelpers();

	const { closeOpenTabs, disableNewTab, tabWhiteList, isExtension, recordWebTraffic } = proctoringOption;
	const isFeatureEnabled = Boolean(closeOpenTabs || disableNewTab || recordWebTraffic);
	const [warningModalShowing, setWarningModalShowing] = useState(false);

	const allowedMap: Record<TabControls, boolean> = {
		closeOpenTabs: isOptionAllowed("closeOpenTabs"),
		disableNewTab: isOptionAllowed("disableNewTab"),
		excludedWebTrafficRecordingUrls: isOptionAllowed("excludedWebTrafficRecordingUrls"),
		recordWebTraffic: isOptionAllowed("recordWebTraffic"),
		tabWhiteList: isOptionAllowed("tabWhiteList"),
	};

	const handleSwitchToggle = useCallback(
		(checked: boolean) => {
			if (!checked) {
				// Turning OFF: Reset everything
				setProctoringOption({
					...proctoringOption,
					closeOpenTabs: false,
					disableNewTab: false,
					recordWebTraffic: false,
					tabWhiteList: [],
					excludedWebTrafficRecordingUrls: [],
				});
			} else {
				// Turning ON: Enable all allowed options by default
				setProctoringOption({
					...proctoringOption,
					closeOpenTabs: allowedMap.closeOpenTabs,
					disableNewTab: allowedMap.disableNewTab,
					recordWebTraffic: allowedMap.recordWebTraffic,
					tabWhiteList: [],
					excludedWebTrafficRecordingUrls: [],
				});
				if (isExtension) setWarningModalShowing(true);
			}
		},
		[
			allowedMap.closeOpenTabs,
			allowedMap.disableNewTab,
			allowedMap.recordWebTraffic,
			isExtension,
			proctoringOption,
			setProctoringOption,
		],
	);

	useEffect(() => {
		const needsUrlCleanup = !closeOpenTabs && !disableNewTab && !recordWebTraffic;
		const hasDataToCleanup = (tabWhiteList?.length ?? 0) > 0;

		if (needsUrlCleanup && hasDataToCleanup) {
			setProctoringOption({
				...proctoringOption,
				tabWhiteList: null,
				tabBlockList: null,
				excludedWebTrafficRecordingUrls: null,
			});
		}
	}, [closeOpenTabs, disableNewTab, proctoringOption, recordWebTraffic, setProctoringOption, tabWhiteList]);

	return (
		<>
			<OptionBlock
				optionKey="tabControl"
				checked={isFeatureEnabled}
				updateState={handleSwitchToggle}
				disabled={
					enforcedProfilesOnly ||
					!(allowedMap["closeOpenTabs"] || allowedMap["disableNewTab"] || allowedMap["recordWebTraffic"])
				}
				disabledMessage={i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disabledReason.instituteDisabled")}
				menuItems={
					<>
						{allowedMap["closeOpenTabs"] && (
							<CheckBoxMenuSubItem
								label={
									<Trans
										i18n={i18nPackage}
										i18nKey={"editQuizPage.configurationStep.otherActivitiesTSX.tabs.closeOpenTabs"}
										components={{ b: <strong /> }}
									/>
								}
								checked={Boolean(closeOpenTabs)}
								onChange={(checked) => {
									setProctoringOption({
										...proctoringOption,
										closeOpenTabs: checked,
									});
								}}
								partiallySupportedPlatforms={getPartiallySupportedPlatforms("closeOpenTabs")}
							/>
						)}
						{allowedMap["disableNewTab"] && (
							<CheckBoxMenuSubItem
								label={
									<Trans
										i18n={i18nPackage}
										i18nKey={"editQuizPage.configurationStep.otherActivitiesTSX.tabs.disableNewTab"}
										components={{ b: <strong /> }}
									/>
								}
								checked={Boolean(disableNewTab)}
								onChange={(checked) => {
									setProctoringOption({
										...proctoringOption,
										disableNewTab: checked,
									});
								}}
								partiallySupportedPlatforms={getPartiallySupportedPlatforms("disableNewTab")}
							/>
						)}
						{allowedMap["recordWebTraffic"] && (
							<CheckBoxMenuSubItem
								label={
									<Trans
										i18n={i18nPackage}
										i18nKey={"editQuizPage.configurationStep.otherActivitiesTSX.tabs.recordWebTraffic"}
										components={{ b: <strong /> }}
									/>
								}
								checked={Boolean(recordWebTraffic)}
								onChange={(checked) => {
									setProctoringOption({
										...proctoringOption,
										recordWebTraffic: checked,
									});
								}}
								partiallySupportedPlatforms={getPartiallySupportedPlatforms("recordWebTraffic")}
							/>
						)}
					</>
				}
			>
				{(allowedMap["tabWhiteList"] || allowedMap["excludedWebTrafficRecordingUrls"]) && isFeatureEnabled && (
					<TabWhiteList
						tabWhiteList={tabWhiteList ?? []}
						onUpdate={(urls) =>
							setProctoringOption({
								...proctoringOption,
								tabWhiteList: allowedMap["tabWhiteList"] ? urls : null,
								excludedWebTrafficRecordingUrls: allowedMap["excludedWebTrafficRecordingUrls"] ? urls : null,
							})
						}
					/>
				)}
			</OptionBlock>
			{warningModalShowing && (
				<IncognitoWarningModal open={warningModalShowing} onClose={() => setWarningModalShowing(false)} />
			)}
		</>
	);
}

export default function OtherActivitiesOptions({ data }: CategoryOptionsListProps) {
	const { shouldShowOption } = useProctoringHelpers();

	return (
		<>
			{(shouldShowOption("disableNewTab") ||
				shouldShowOption("closeOpenTabs") ||
				shouldShowOption("tabWhiteList") ||
				shouldShowOption("recordWebTraffic")) && <TabControlsOption />}
			{shouldShowOption("oneScreenEnforcementMode") && <OneScreenOption />}
			{shouldShowOption("detectSidePanel") && <DetectSidePanelOption />}

			{shouldShowOption("logInstalledExtensions") && <LogInstalledExtensionsOption />}

			{shouldShowOption("enableGeofence") && <EnableGeofenceOption savedGeofences={data.geofences} />}

			{shouldShowOption("recordInput") && <RecordInputOption />}

			{shouldShowOption("recordClipboard") && <RecordClipboardOption />}

			{shouldShowOption("uploadScratchNotes") && <UploadScratchNotesOption />}

			{shouldShowOption("restrictDevices") && <RestrictDevicesOption />}
		</>
	);
}
